import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { authApi, type SessionUser } from './api/auth';
import { ApiError, onUnauthorized } from './api/client';

export interface Session {
  /** undefined while the first probe runs; null when signed out. */
  readonly user: SessionUser | null | undefined;
  /** Platform grants of the user (UX only: the API authorizes every call). */
  readonly platformPermissions: readonly string[];
  readonly platformLoading: boolean;
  canPlatform(permission: string): boolean;
  signOut(): Promise<void>;
}

const SessionContext = createContext<Session | null>(null);

export const sessionKeys = {
  session: ['session'] as const,
  platform: (userId: string) => ['platform-permissions', userId] as const,
};

/** The permissions that open the admin area (any one of them). */
export const ADMIN_PERMISSIONS = [
  'moderation.queue.read',
  'moderation.rules.manage',
  'garrabha_admin.read',
] as const;

export function SessionProvider(props: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const sessionQuery = useQuery({
    queryKey: sessionKeys.session,
    queryFn: async ({ signal }) => {
      try {
        return (await authApi.session(signal)).user;
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) return null;
        throw error;
      }
    },
    staleTime: 5 * 60_000,
  });
  const user = sessionQuery.data;

  useEffect(() => {
    onUnauthorized(() => {
      queryClient.setQueryData(sessionKeys.session, null);
      queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== 'session' });
    });
    return () => onUnauthorized(null);
  }, [queryClient]);

  const platformQuery = useQuery({
    queryKey: sessionKeys.platform(user?.id ?? 'anonymous'),
    queryFn: ({ signal }) => authApi.platformPermissions(signal).then((r) => r.permissions),
    enabled: Boolean(user),
    staleTime: 5 * 60_000,
  });

  const signOut = useCallback(async () => {
    try {
      await authApi.logout();
    } finally {
      queryClient.setQueryData(sessionKeys.session, null);
      queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== 'session' });
    }
  }, [queryClient]);

  const platformSet = useMemo(() => new Set(platformQuery.data ?? []), [platformQuery.data]);

  const value = useMemo<Session>(
    () => ({
      user: sessionQuery.isPending ? undefined : (user ?? null),
      platformPermissions: platformQuery.data ?? [],
      platformLoading: Boolean(user) && platformQuery.isPending,
      canPlatform: (permission) => platformSet.has(permission),
      signOut,
    }),
    [
      sessionQuery.isPending,
      user,
      platformQuery.data,
      platformQuery.isPending,
      platformSet,
      signOut,
    ],
  );

  return <SessionContext.Provider value={value}>{props.children}</SessionContext.Provider>;
}

export function useSession(): Session {
  const value = useContext(SessionContext);
  if (!value) throw new Error('Wrap the app in <SessionProvider>');
  return value;
}

/** Where to go after sign-in: only paths on this site. */
export function safeNext(value: string | null): string {
  return value?.startsWith('/') && !value.startsWith('//') ? value : '/';
}

export function loginPath(next: string): string {
  return next === '/' ? '/login' : `/login?next=${encodeURIComponent(next)}`;
}

export function useCountdown(seconds: number) {
  const [left, setLeft] = useState(seconds);
  const [run, setRun] = useState(0);
  useEffect(() => {
    setLeft(seconds);
    const timer = setInterval(() => setLeft((value) => Math.max(0, value - 1)), 1000);
    return () => clearInterval(timer);
  }, [seconds, run]);
  return { left, restart: () => setRun((value) => value + 1) };
}
